import React, { useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { dateTimeToString } from 'common/utils/date/Date.utils';
import moment from 'moment';
import { dataToSelect } from '../../common/helpers';
import {
  FormDatePickerField,
  FormInputField,
  FormInputFieldType,
  FormSelectField,
} from '../../common/components/formFields';
import { getVehicleMakeList, getVehicleModelsList } from '../VehicleActions';
import { bodyTypes } from '../../constants/bodyTypes';
import { carColours } from '../../constants/carColours';
import { fuelTypes } from '../../constants/fuelTypes';
import { transmissionTypes } from '../../constants/transmissionTypes';
import { previousOwnersTypes } from '../../constants/previousOwnersTypes';
import { booleanOptions } from '../../constants/BooleanOptions';
import { VehicleTypes } from '../../constants/VehicleTypes';

const colourOptions = dataToSelect(
  carColours.map((value) => ({ value, label: value })),
  ['value', 'label'],
);
const fuelOptions = dataToSelect(
  fuelTypes.map((value) => ({ value, label: value })),
  ['value', 'label'],
);
const transmissionOptions = dataToSelect(
  transmissionTypes.map((value) => ({ value, label: value })),
  ['value', 'label'],
);
const vehicleTypesOptions = dataToSelect(VehicleTypes, ['value', 'label']);
const bodyTypeOptions = dataToSelect(
  bodyTypes.map((value) => ({ value, label: value })),
  ['value', 'label'],
);
const previousOwnersOptions = dataToSelect(previousOwnersTypes, ['value', 'label']);

function VehicleDetailsForm({ form }) {
  const organizationLocations = useSelector((state) =>
    state.vehicles.get('auctionDetails'),
  )?.organizationLocations;
  const dispatch = useDispatch();
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [location, setLocation] = useState([]);

  useEffect(() => {
    dispatch(
      getVehicleMakeList((makes) => {
        setMakes(dataToSelect(makes.data, ['id', 'name']));
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (organizationLocations?.length > 0) {
      const preparedOrganizationLocations = organizationLocations.map((item) => ({
        label: item?.postalCode?.postcode,
        value: item.id,
      }));
      setLocation(preparedOrganizationLocations);
    }
  }, []);

  useEffect(() => {
    if (form.values.make?.value && models.length === 0) {
      dispatch(
        getVehicleModelsList(form.values.make.value, (models) => {
          setModels(dataToSelect(models.data, ['id', 'name']));
        }),
      );
    }
  }, [form.values.make, models.length, dispatch]);

  const handlerMakeChange = (make) => {
    if (make?.value) {
      form.setFieldValue('model', '');
      dispatch(
        getVehicleModelsList(make.value, (models) => {
          setModels(dataToSelect(models.data, ['id', 'name']));
        }),
      );
    }
  };

  return (
    <>
      <Row>
        <Col md="4">
          <FormInputField name="launchingCounterSerial" label="Offer ID" disabled required />
        </Col>
        <Col md="4">
          <FormInputField name="status" label="Status" disabled required />
        </Col>
        <Col md="4">
          <FormInputField name="numberPlate" label="Number Plate" required />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormInputField name="dealer" label="Seller" disabled required />
        </Col>
        <Col md="4">
          <FormSelectField
            name="make"
            label="Make"
            onChangeHandler={handlerMakeChange}
            options={makes}
            required
          />
        </Col>
        <Col md="4">
          <FormSelectField name="model" label="Model" options={models} required />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormSelectField
            name="vehicleType"
            label="Vehicle Type"
            options={vehicleTypesOptions}
            required
          />
        </Col>
        <Col md="4">
          <FormSelectField
            name="vatQualifying"
            label="Vat Qualifying"
            options={booleanOptions}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormInputField name="year" label="Year" type={FormInputFieldType.NUMBER} required />
        </Col>
        <Col md="4">
          <FormInputField
            name="mileage"
            label="Mileage"
            type={FormInputFieldType.NUMBER}
            required
          />
        </Col>
        <Col md="4">
          <FormInputField name="derivative" label="Derivative" upperCase required />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormSelectField
            name="carType"
            label="Body Type"
            options={bodyTypeOptions}
            required
          />
        </Col>
        <Col md="4">
          <FormSelectField
            name="transmission"
            label="Transmission"
            options={transmissionOptions}
            required
          />
        </Col>
        <Col md="4">
          <FormInputField
            name="engineSize"
            label="Engine Size"
            required
            type={FormInputFieldType.NUMBER}
          />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormSelectField name="fuel" label="Fuel" options={fuelOptions} required />
        </Col>
        <Col md="4">
          <FormSelectField name="colour" label="Colour" options={colourOptions} required />
        </Col>
        <Col md="4">
          <FormInputField name="doors" label="Doors" required />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormInputField
            name="previousOwners"
            label="Previous Keepers"
            type={FormInputFieldType.NUMBER}
            required
          />
        </Col>
        <Col md="4">
          <FormDatePickerField name="mot" label="MOT" readOnly />
        </Col>
        <Col md="4">
          <FormSelectField
            name="v5cLogbook"
            label="V5C Logbook Present"
            options={booleanOptions}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormSelectField
            name="ulezCompliant"
            label="ULEZ Compliant"
            options={booleanOptions}
            required
          />
        </Col>
        <Col md="4">
          <FormSelectField
            name="numbersOfKeyIncluded"
            label="Numbers of Key Included"
            options={previousOwnersOptions}
          />
        </Col>
        <Col md="4">
          <FormDatePickerField name="registrationDate" label="Registration Date" required />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormDatePickerField
            name="launchDate"
            label="Launch Date"
            disabled
            format={(value) => (value ? moment(value) : '')}
            required
          />
        </Col>
        <Col md="4">
          <FormInputField name="vin" label="VIN/Chassis No." required />
        </Col>
        <Col md="4">
          <FormInputField name="seller" label="User" disabled required />
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormSelectField
            name="organizationLocations"
            label="Location Name"
            options={location}
            required
          />
        </Col>
        <Col md="4">
          <FormDatePickerField
            name="deletedOn"
            label="Deleted On"
            disabled
            format={(value) => dateTimeToString(value)}
          />
        </Col>
      </Row>
    </>
  );
}

VehicleDetailsForm.propTypes = {
  form: PropTypes.object.isRequired,
};

export default VehicleDetailsForm;
